/**
 * CrossReferenceBlock — inline parallel-time link inside chain step content.
 *
 * Lead Foundation Brief §6: cross-references assert "this is happening at
 * the same time over there". Visually distinct from citations and from
 * picture-show elements. Source citations are required (the platform never
 * lets cross-references be casual claims). Click navigates to the target
 * chain — and now (Apr 29 2026) fires a Three.js GlobeArcTransition that
 * traces a great-circle from the current chain's origin to the target
 * chain's origin BEFORE the navigation lands, so the spatial relationship
 * is felt before it's read.
 */
import { useState } from 'react';
import { ArrowRight, GitBranch, BookOpen } from 'lucide-react';
import api from '../../lib/api';
import GlobeArcTransition from '../GlobeArcTransition';
import { resolveEraOrigin } from '../../lib/eraOrigins';

const RELATIONSHIP_LABELS = {
  parallel_in_time: 'Happening at the same time',
  consequence_of: 'Direct consequence of',
  precursor_to: 'Precursor to',
  parallel_pattern: 'Same pattern, different place',
};

export default function CrossReferenceBlock({ refs, era, onNavigate, fromOrigin, fromLabel }) {
  const [arc, setArc] = useState(null); // { fromLat, fromLon, toLat, toLon, toLabel, target }

  if (!refs || refs.length === 0) return null;

  const handleJump = async (targetId) => {
    // Resolve the target chain's origin lat/lon. We try /api/chains/{id}
    // first (gets the era), then fall back to the era's canonical
    // origin from /lib/eraOrigins. Worst case (network failure) we
    // skip the arc and navigate immediately so the user is never
    // stranded watching a spinner.
    let targetOrigin = null;
    let targetLabel = '';
    try {
      const r = await api.get(`/chains/${targetId}`);
      targetOrigin = resolveEraOrigin(r.data);
      targetLabel = r.data?.title || '';
    } catch {
      targetOrigin = null;
    }
    if (!targetOrigin || !fromOrigin) {
      onNavigate?.({ id: targetId });
      return;
    }
    setArc({
      fromLat: fromOrigin.lat,
      fromLon: fromOrigin.lon,
      toLat: targetOrigin.lat,
      toLon: targetOrigin.lon,
      fromLabel: fromLabel || '',
      toLabel: targetLabel || targetOrigin.label || '',
      target: targetId,
    });
  };

  return (
    <>
      {arc && (
        <GlobeArcTransition
          fromLat={arc.fromLat}
          fromLon={arc.fromLon}
          toLat={arc.toLat}
          toLon={arc.toLon}
          fromLabel={arc.fromLabel}
          toLabel={arc.toLabel}
          durationMs={1900}
          fadeOutMs={500}
          onComplete={() => {
            const tid = arc.target;
            setArc(null);
            onNavigate?.({ id: tid });
          }}
          testId="cross-ref-arc"
        />
      )}
      <div className="my-6 space-y-3" data-testid="cross-references">
        {refs.map((r, i) => (
          <aside
            key={r.id || i}
            className="border-l-2 rounded-r-md p-4"
            style={{
              borderColor: era?.primary || '#2F8A92',
              background: `linear-gradient(90deg, ${(era?.primary || '#2F8A92')}0F, transparent 70%)`,
            }}
            data-testid={`cross-ref-${i}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <GitBranch size={13} style={{ color: era?.primary || '#2F8A92' }} />
              <span
                className="font-hud text-[9px] md:text-[10px] tracking-[0.18em] uppercase"
                style={{ color: era?.primary || '#2F8A92' }}
              >
                {RELATIONSHIP_LABELS[r.relationship_type] || 'Cross-reference'}
              </span>
            </div>
            <p
              className="font-historical text-base md:text-lg leading-snug"
              style={{ color: era?.text || '#e8d5a3' }}
            >
              {r.assertion_text}
            </p>
            {(r.source_citations || []).length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {r.source_citations.map((c, ci) => (
                  <li key={ci} className="font-mono text-[10px] md:text-xs text-white/65 inline-flex items-center gap-1">
                    <BookOpen size={10} className="opacity-65" />
                    {c.label || c.url || c.citation}
                  </li>
                ))}
              </ul>
            )}
            {(r.target_chain_ids || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {r.target_chain_ids.map((tid) => (
                  <button
                    key={tid}
                    data-testid={`cross-ref-link-${tid}`}
                    onClick={() => handleJump(tid)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-hud text-[9px] md:text-[10px] tracking-widest transition-colors"
                    style={{
                      color: era?.primary || '#2F8A92',
                      border: `1px solid ${era?.primary || '#2F8A92'}55`,
                      background: 'transparent',
                    }}
                  >
                    WALK THIS CHAIN <ArrowRight size={11} />
                  </button>
                ))}
              </div>
            )}
          </aside>
        ))}
      </div>
    </>
  );
}
